
import React, { useEffect } from 'react';
import { UploadProvider } from './upload/contexts/UploadContext';
import { VisibilityChoice } from './upload/VisibilityChoice';
import { SourceChoice } from './upload/SourceChoice';
import { FileUpload } from './upload/FileUpload';
import { ExternalUrlInput } from './upload/ExternalUrlInput';
import PaperMetadataForm from './upload/PaperMetadataForm';
import { ReviewRequestSection } from './upload/ReviewRequestSection';
import { FormSubmitSection } from './upload/FormSubmitSection';
import { useUploadContext } from './upload/contexts/UploadContext';
import AuthModal from '@/components/auth/AuthModal';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Sparkles } from 'lucide-react';

const UploadPaperForm: React.FC = () => {
  const { 
    visibilityChoice,
    setVisibilityChoice,
    sourceChoice,
    setSourceChoice,
    shouldShowMetadataForm, 
    handleSubmit, 
    title, 
    setTitle, 
    abstract, 
    setAbstract, 
    category, 
    setCategory, 
    keywords, 
    setKeywords,
    additionalAuthors,
    setAdditionalAuthors,
    visibility,
    setVisibility,
    aiReviewUsage,
    file,
    setFile,
    externalUrl,
    setExternalUrl,
    validationStatus,
    validationMessage,
    validationErrors,
    validateExternalUrl,
    metadata,
    license,
    licenseCompliant,
    licenseValidationError,
    duplicateInfo,
    setUploadMethod,
    showAuthModal,
    setShowAuthModal,
    handleAuthSuccess
  } = useUploadContext();

  const [metadataOpen, setMetadataOpen] = React.useState(false);

  // Auto-set upload method and visibility based on choices
  useEffect(() => {
    if (visibilityChoice === 'private') {
      setVisibility('private');
      setSourceChoice('local');
      setUploadMethod('file');
    } else if (visibilityChoice === 'public') {
      setVisibility('public');
      if (sourceChoice === 'arxiv') {
        setUploadMethod('arxiv');
      } else if (sourceChoice === 'local') {
        setUploadMethod('file');
      }
    }
  }, [visibilityChoice, sourceChoice, setVisibility, setSourceChoice, setUploadMethod]);

  const handleBackToVisibility = () => {
    setVisibilityChoice(null);
    setSourceChoice(null);
  };

  const handleBackToSource = () => {
    setSourceChoice(null);
  };

  // Step-based rendering logic
  const showVisibilityChoice = visibilityChoice === null;
  const showSourceChoice = visibilityChoice === 'public' && sourceChoice === null;
  const showUploadArea = (
    (visibilityChoice === 'private') || 
    (visibilityChoice === 'public' && sourceChoice !== null)
  );

  const isPrivateFlow = visibilityChoice === 'private';

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Visibility Choice - Always shown first */}
        {showVisibilityChoice && (
          <VisibilityChoice
            visibilityChoice={visibilityChoice}
            setVisibilityChoice={setVisibilityChoice}
            aiReviewUsage={aiReviewUsage}
          />
        )}

        {/* Step 2: Source Choice - Only for public papers */}
        {showSourceChoice && (
          <SourceChoice
            sourceChoice={sourceChoice}
            setSourceChoice={setSourceChoice}
            onBack={handleBackToVisibility}
          />
        )}

        {/* Step 3: Upload/Input Area */}
        {showUploadArea && (
          <div className="space-y-8 animate-fade-in">
            {/* Show selected choices summary */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground pb-4 border-b border-border/50">
              <span className="font-medium text-foreground">
                {visibilityChoice === 'private' ? '🛡️ Confidential Draft' : '🌍 Public Paper'}
              </span>
              {visibilityChoice === 'public' && sourceChoice && (
                <>
                  <span>•</span>
                  <span className="font-medium text-foreground">
                    {sourceChoice === 'arxiv' ? '📄 arXiv Link' : '💻 Local Upload'}
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={handleBackToVisibility}
                className="ml-auto text-primary hover:underline"
              >
                Change
              </button>
            </div>

            {/* File Upload or External URL Input */}
            {(visibilityChoice === 'private' || sourceChoice === 'local') && (
              <FileUpload file={file} setFile={setFile} />
            )}

            {visibilityChoice === 'public' && sourceChoice === 'arxiv' && (
              <ExternalUrlInput
                externalUrl={externalUrl}
                setExternalUrl={setExternalUrl}
                validationStatus={validationStatus}
                validationMessage={validationMessage}
                validationErrors={validationErrors}
                validateExternalUrl={validateExternalUrl}
                metadata={metadata}
                license={license}
                licenseCompliant={licenseCompliant}
                licenseValidationError={licenseValidationError}
                duplicateInfo={duplicateInfo}
              />
            )}

            {/* Paper Metadata Section */}
            {isPrivateFlow && file && (
              <>
                {/* Auto-extract info note */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border/50">
                  <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">AI will extract the details</p>
                    <p>Title, abstract, and authors will be automatically extracted from your PDF after upload. You can also add them manually below.</p>
                  </div>
                </div>

                {/* Collapsible metadata form */}
                <Collapsible open={metadataOpen} onOpenChange={setMetadataOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${metadataOpen ? 'rotate-180' : ''}`} />
                      Add details manually
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <div className="border-t border-border/50 pt-4">
                      <PaperMetadataForm 
                        title={title}
                        setTitle={setTitle}
                        abstract={abstract}
                        setAbstract={setAbstract}
                        category={category}
                        setCategory={setCategory}
                        keywords={keywords}
                        setKeywords={setKeywords}
                        additionalAuthors={additionalAuthors}
                        setAdditionalAuthors={setAdditionalAuthors}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </>
            )}

            {/* Public flow: always show metadata form */}
            {!isPrivateFlow && shouldShowMetadataForm && (
              <div className="pt-4 border-t border-border/50">
                <PaperMetadataForm 
                  title={title}
                  setTitle={setTitle}
                  abstract={abstract}
                  setAbstract={setAbstract}
                  category={category}
                  setCategory={setCategory}
                  keywords={keywords}
                  setKeywords={setKeywords}
                  additionalAuthors={additionalAuthors}
                  setAdditionalAuthors={setAdditionalAuthors}
                />
              </div>
            )}
            
            {/* Review Request Section - Only show if metadata form is visible and public */}
            {shouldShowMetadataForm && visibilityChoice === 'public' && (
              <ReviewRequestSection />
            )}
            
            {/* Submit Section */}
            <FormSubmitSection />
          </div>
        )}
      </form>
      
      {/* Auth Modal for unsigned users */}
      <AuthModal 
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        onAuthSuccess={handleAuthSuccess}
        message="Create an account to publish your paper"
      />
    </>
  );
};

const UploadPaper: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <UploadProvider>
        <UploadPaperForm />
      </UploadProvider>
    </div>
  );
};

export default UploadPaper;
